<template>
    <Teleport to="body">
        <div
            :class="[$style.container, guideClosed && $style.right, $style[i18n.global.locale]]"
            v-if="guideVisible && fetchCategoryListQueryState === 'success'"
            v-clickoutside="handleGuideOutSideClick"
        >
            <GuidePopover @close="handleGuideCloseClick"></GuidePopover>

            <div
                v-if="!guideClosed && visibleGuideIcon"
                :class="[
                    $style.start,
                    $style.animate__animated,
                    guideIconZoomOut && $style.animate__slideOutRight
                ]"
                @click="triggerGuidePopover"
            >
                <div :class="$style.close" v-if="!guideIconZoomOut" @click.stop="handleCloseClick">
                    <pt-icon icon="pt-icon-close"></pt-icon>
                </div>
                <pt-progress
                    type="circle"
                    color="#29B6F6"
                    :width="34"
                    :stroke-width="2"
                    :indeterminate="true"
                    :percentage="percentage"
                >
                    <pt-icon
                        v-if="allStepCompleted"
                        icon="pt-icon-solidtick"
                        :style="{
                            width: '16px',
                            height: '16px',
                            fill: '#29B6F6'
                        }"
                    ></pt-icon>
                    <span v-else :data-percentage="percentage" :class="$style.progress">
                        {{ percentage }}%
                    </span>
                </pt-progress>

                <span v-if="!guideIconZoomOut">{{ $t('guide.start_guide') }}</span>
            </div>
        </div>
    </Teleport>
    <TrackingCodeNoticeDialog
        v-if="guideTrackCodeDialogVisible"
        :currentGuideStepKey="currentGuideStepKey"
        @close="guideTrackCodeDialogVisible = false"
    ></TrackingCodeNoticeDialog>
</template>

<script lang="ts">
import clickoutside from '@/components/ptengine-ui/components/directives/click-outside';

export default {
    name: 'Guide',
    directives: { clickoutside }
};
</script>

<script lang="ts" setup>
import { watch, ref, computed, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import i18n from '@assets/i18n/index';
import { useGuideStore, GuideKey } from '@master/store/guide.store';
import { isSuperAccount } from '@models/account/useAccountInfo';
import { usePermission } from '@models/profile/useProfilePermission';
import useLocalData from '@hooks/useLocalData';
import GuidePopover from './GuidePopover.vue';
import TrackingCodeNoticeDialog from '@master/components/dialog/TrackingCodeNoticeDialog.vue';
import useEventBus from '@assets/plugins/eventBus';

// state
const route = useRoute();
const { eventBus } = useEventBus();
const { hasExperienceV4Permission } = usePermission();
const { updateLocalData } = useLocalData();
const {
    allStepCompleted,
    fetchCategoryListQueryState,
    guideClosed,
    guideOnboardingTypeName,
    percentage,
    visibleGuideIcon
} = storeToRefs(useGuideStore());
const {
    hideGuidePopover,
    triggerGuidePopover,
    triggerGuideIcon,
    updateGuideVisibleStatus,
    trackEvent
} = useGuideStore();
const guideCompleteVisible = ref(false);
const guideIconZoomOut = ref(false); // Guide icon缩小动画
const guideTrackCodeDialogVisible = ref(false);
const currentGuideStepKey = ref('');
/**
 * 入口显示条件
 * 1. 非超级账号
 * 2. 且已选择档案
 * 3. 且有 Experience V4 权限
 * 4. 且不在编辑器中
 */
const guideVisible = computed(() => {
    return (
        !isSuperAccount.value &&
        Boolean(route.params.sid) &&
        Boolean(hasExperienceV4Permission.value) &&
        route.name !== 'ExperienceEditor'
    );
});

onMounted(() => {
    eventBus.$on('iFrameClick', function () {
        handleGuideOutSideClick();
    });
    eventBus.$on('showTrackCodeDialog', handleShowTrackCodeDialog, false);
});

onUnmounted(() => {
    eventBus.$off('showTrackCodeDialog', handleShowTrackCodeDialog, false);
});

/**
 * 监听隐藏状态
 * 需立即执行更新store中的状态值
 */
watch(
    guideVisible,
    () => {
        if (guideVisible.value) {
            guideCompleteVisible.value = false;
            hideGuidePopover();
        }
        updateGuideVisibleStatus(guideVisible.value);
    },
    {
        immediate: true
    }
);

watch(
    route,
    (curRoute, preRoute) => {
        triggerGuideIcon(true);
    },
    {
        deep: true
    }
);

function handleShowTrackCodeDialog(guideKey: GuideKey) {
    guideTrackCodeDialogVisible.value = true;
    currentGuideStepKey.value = guideKey;
}

function handleGuideCloseClick() {
    updateLocalData('startGuideClosed', {
        guideClosed: true
    });
    hideGuidePopover();
    guideClosed.value = true;
}

/**
 * 当在非 onboarding 模式下，点击外部自动收起
 */
function handleGuideOutSideClick() {
    const inOnboarding = document.querySelector(
        `[data-onboarding-type="${guideOnboardingTypeName.value}"]`
    );

    if (!inOnboarding) {
        hideGuidePopover();
    }
}

/**
 * 关闭icon点击
 */
function handleCloseClick() {
    guideIconZoomOut.value = true;

    // 800ms： 动画
    setTimeout(function () {
        triggerGuideIcon(false);
        guideIconZoomOut.value = false;
    }, 800);

    hideGuidePopover();
    trackEvent('guide_temporary_close', {
        complete_rate: percentage.value + '%'
    });
}
</script>

<style lang="scss" module>
@import '@style/import.scss';

:root {
    --animate-duration: 0.4s;
    --animate-delay: 0.4s;
    --animate-repeat: 1;
}
.animate__animated {
    -webkit-animation-duration: var(--animate-duration);
    animation-duration: var(--animate-duration);
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
}

@-webkit-keyframes zoomOutRight {
    40% {
        opacity: 1;
        -webkit-transform: scale3d(0.475, 0.475, 0.475) translate3d(-42px, 0, 0);
        transform: scale3d(0.475, 0.475, 0.475) translate3d(-42px, 0, 0);
    }
    to {
        opacity: 0;
        -webkit-transform: scale(0.1) translate3d(360px, 0, 0);
        transform: scale(0.1) translate3d(360px, 0, 0);
    }
}

@keyframes zoomOutRight {
    40% {
        opacity: 1;
        -webkit-transform: scale3d(0.475, 0.475, 0.475) translate3d(-42px, 0, 0);
        transform: scale3d(0.475, 0.475, 0.475) translate3d(-42px, 0, 0);
    }
    to {
        opacity: 0;
        -webkit-transform: scale(0.1) translate3d(360px, 0, 0);
        transform: scale(0.1) translate3d(360px, 0, 0);
    }
}

@-webkit-keyframes slideOutRight{
    0% {
        -webkit-transform:translateZ(0);
        transform:translateZ(0);
        background-color: transparent;
        width: 40px;
        height: 40px;
        border-radius: 50%;
    }
    to {
        visibility:hidden;
        -webkit-transform:translate3d(100%,0,0);
        transform:translate3d(100%,0,0);
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: transparent;
        transform: translateX(60px);
    }
}
@keyframes slideOutRight{
    0% {
        -webkit-transform:translateZ(0);
        transform:translateZ(0);
        background-color: transparent;
        width: 40px;
        height: 40px;
        border-radius: 50%;
    }
    to {
        visibility:hidden;
        -webkit-transform:translate3d(100%,0,0);
        transform:translate3d(100%,0,0);
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: transparent;
        transform: translateX(60px);
    }
}

.start {
    cursor: pointer;
    @extend %flex-center;

    background: $pt-white;
    box-shadow: 0px 4px 24px 2px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 5px 12px;
    transition: 0.3s all;

    &:hover {
        background-color: $pt-black-10;
        .close {
            visibility: visible;
        }
    }

    &.hide {
        display: none;
    }

    &.animate__zoomOutRight {
        -webkit-animation-name: zoomOutRight;
        animation-name: zoomOutRight;
        -webkit-transform-origin: right center;
        transform-origin: right center;
    }

    &.animate__slideOutRight{
        -webkit-animation-name:slideOutRight;
        animation-name:slideOutRight
    }

    :global {
        .pt-progress__text {
            min-width: auto;
        }
    }

    > span {
        margin-left: 8px;
        font-weight: 500;
        font-size: 14px;
        line-height: 150%;
        color: $pt-black-900;
    }

    .progress {
        color: $pt-info;
        font-weight: 400;
        font-size: 12px !important;
        line-height: 150%;

        &[data-percentage='0'] {
            color: $pt-grey8;
        }
    }

    .close {
        visibility: hidden;
        @extend %flex-center;
        width: 20px;
        height: 20px;
        background: $pt-white;
        border: 1px solid $pt-grey13;
        cursor: pointer;
        position: absolute;
        top: -6px;
        right: -6px;
        border-radius: 50%;
        overflow: hidden;

        :hover {
            background-color: $pt-black-20;
        }

        svg {
            width: 16px;
            height: 16px;
            fill: $pt-black-90;
        }
    }
}

.container {
    position: fixed;
    z-index: calc(#{$page-help-index} - 1);
    bottom: $page-help-icon-margin-bottom;
    right: calc(#{$page-help-icon-margin-right} + #{$page-help-icon-width} + 20px);
    min-height: $page-help-icon-width;

    &.EN {
        > div::after {
            right: 120px;
        }
    }

    &.JP {
        > div::after {
            right: 132px;
        }
    }

    &.ZH {
        > div::after {
            right: 110px;
        }
    }

    &.right {
        right: $page-help-icon-margin-right;

        ::after {
            right: calc(#{$page-help-icon-width} / 2 - 6px);
        }

        > div {
            right: 0;
            &::after {
                right: calc(#{$page-help-icon-width} / 2 - 6px);
            }
        }
    }
}
</style>
